/* eslint-disable max-lines-per-function */
import { useCallback, useEffect, useMemo, useState } from "react";
import { DetailedTableInfoDc } from "@evergis/api/dist/__generated__/data-contracts";
import { changeProps, removeObject, returnFound } from "find-and";
import { Permissions } from "@evergis/api";

import { isConfigure, togglePermissions } from "../../../../../utils/permissions";

export const useStructure = (initialStructure: DetailedTableInfoDc, activeUser: string, loading?: boolean) => {
  const [structure, setStructure] = useState<DetailedTableInfoDc | undefined>();

  const currentTablePermissions = useMemo(
    () => structure?.acl.data.find(item => item.role === `__${activeUser}`)?.permissions || Permissions.None,
    [activeUser, structure],
  );

  useEffect(() => {
    if (initialStructure !== undefined && structure === undefined && !loading) {
      setStructure(initialStructure);
    }
  }, [structure, initialStructure, loading]);

  // Коллбэк, вызываемый при добавлении пользователя из выпадающего списка
  const onUserPermissionsInit = useCallback(
    (userRole: string) => {
      if (!structure) {
        return;
      }

      setStructure({
        ...structure,
        acl: {
          ...structure.acl,
          data: !returnFound(structure.acl.data, { role: userRole })
            ? [
                ...structure.acl.data,
                {
                  role: userRole,
                  permissions: Permissions.Read,
                },
              ]
            : isConfigure(structure.permissions)
            ? changeProps(structure.acl.data, { role: userRole }, { permissions: Permissions.Read })
            : structure.acl.data,
        },
      });
    },
    [structure],
  );

  // Коллбэк, вызываемый при удалении пользователя (клик по крестику в соответствующем чипе)
  const onUserPermissionsRemove = useCallback(
    (userRole: string) => {
      if (!structure) {
        return;
      }

      setStructure({
        ...structure,
        acl: {
          ...structure.acl,
          data: removeObject(structure.acl.data, { role: userRole }),
        },
      });
    },
    [structure],
  );

  // Обработка клика на переключатели типов доступа
  const onUpdateTableAcl = useCallback(
    (toggle: "read" | "write" | "configure") => {
      if (!structure) {
        return;
      }

      setStructure({
        ...structure,
        acl: {
          ...structure.acl,
          data: changeProps(
            structure.acl.data,
            { role: `__${activeUser}` },
            {
              permissions: togglePermissions(currentTablePermissions, toggle),
            },
          ),
        },
      });
    },
    [activeUser, currentTablePermissions, structure],
  );

  // Обогащение acl.data при выборе пользователя:
  // Если данные для пользователя отсутствуют -- добавляем None для него
  const enrichAclData = useCallback(
    (inputUser: string) => {
      if (!structure) {
        return;
      }

      setStructure({
        ...structure,
        acl: {
          data: !returnFound(structure.acl.data, { role: `__${inputUser}` })
            ? [
                ...structure.acl.data,
                {
                  role: `__${inputUser}`,
                  permissions: Permissions.None,
                },
              ]
            : structure.acl.data,
        },
      });
    },
    [structure],
  );

  return {
    structure,
    onUserPermissionsInit,
    onUserPermissionsRemove,
    onUpdateTableAcl,
    enrichAclData,
    currentTablePermissions,
  } as const;
};
